import clsx from 'clsx'
import EmptyResults from 'components/EmptyResults'
import GridSpinner from 'components/UI/GridSpinner'
import { Icon } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'
import { useFlexLayout, useSortBy, useTable } from 'react-table'
import { useSticky } from 'react-table-sticky'
import RemoteHeader from 'components/Table/RemoteHeader'
import styles from 'components/Table/table.module.scss'

const TableSticky = ({
  columns,
  tableData,
  tableLoading,
  className,
  tableConfig,
  onSortingChange = () => {},
}) => {
  let scrollX = 0

  const tableRef = useRef()
  const tableHeaderRef = useRef()

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: tableData,
      ...tableConfig,
      manualSorting: true,
    },
    useSortBy,
    useFlexLayout,
    useSticky,
  )

  useEffect(() => {
    onSortingChange(sortBy)
  }, [sortBy])

  const footerGroups = headerGroups.slice().reverse()

  const handleScroll = (e) => {
    if (e.currentTarget.scrollLeft !== scrollX) {
      scrollX = e.currentTarget.scrollLeft

      if (tableRef.current) {
        tableRef.current.scrollLeft = scrollX
        tableRef.current.classList.toggle(
          'scrolled',
          tableRef.current.scrollLeft != 0,
        )
      }

      if (tableHeaderRef.current) {
        tableHeaderRef.current.scrollLeft = scrollX
        tableHeaderRef.current.classList.toggle(
          'scrolled',
          tableRef.current.scrollLeft != 0,
        )
      }
    }
  }

  useEffect(() => {
    tableRef.current &&
      tableRef.current.addEventListener('scroll', handleScroll)
    tableHeaderRef.current &&
      tableHeaderRef.current.addEventListener('scroll', handleScroll)
    return () => {
      tableRef.current &&
        tableRef.current.removeEventListener('scroll', handleScroll)
      tableHeaderRef.current &&
        tableHeaderRef.current.removeEventListener('scroll', handleScroll)
    }
  }, [tableRef.current])

  if (tableData.length === 0) {
    return tableLoading ? (
      <GridSpinner />
    ) : (
      <div className={styles['table_empty']}>
        <EmptyResults />
      </div>
    )
  }

  return (
    <>
      <RemoteHeader>
        <div
          {...getTableProps([{ style: { minWidth: 'auto' } }])}
          className={clsx(
            styles['table_sticky'],
            className,
            styles['table-header_sticky'],
            'table-header_sticky',
          )}
          ref={tableHeaderRef}
        >
          {headerGroups.map((headerGroup, headerGroupIndex) => (
            <div
              key={'header-group-' + headerGroupIndex}
              {...headerGroup.getHeaderGroupProps()}
              className={styles['header']}
            >
              {headerGroup.headers.map((column, columnIndex) => (
                <div
                  key={`header-group-${headerGroupIndex}-column-${columnIndex}`}
                  {...column.getHeaderProps([
                    {
                      className: clsx(column.className, {
                        [styles['column-header_active']]: column.isSorted,
                        [styles['column-header--inactive']]: !column.isSorted,
                      }),
                      style: column.style,
                    },
                    column.getSortByToggleProps(),
                  ])}
                >
                  {column.render('Header')}
                  {column.canSort && (
                    <span
                      className={clsx(
                        'sorting-arrow',
                        styles['sorting-arrow'],
                        {
                          [styles['sorting-arrow--inactive']]: !column.isSorted,
                          [styles['sorting-arrow--desc']]: column.isSortedDesc,
                          [styles['sorting-arrow--asc']]: !column.isSortedDesc,
                        },
                      )}
                    >
                      <Icon
                        name={'arrow2-big'}
                        size={'s'}
                        className={clsx({
                          [styles['sorting-arrow_desc']]: !column.isSortedDesc,
                        })}
                      />
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </RemoteHeader>

      <div
        {...getTableProps([{ style: { minWidth: 'auto' } }])}
        className={clsx(styles['table_sticky'], className)}
        ref={tableRef}
      >
        {headerGroups.map((headerGroup, headerGroupIndex) => (
          <div
            key={'header-group-' + headerGroupIndex}
            {...headerGroup.getHeaderGroupProps()}
            className={styles['header']}
          >
            {headerGroup.headers.map((column, columnIndex) => (
              <div
                key={`header-group-${headerGroupIndex}-column-${columnIndex}`}
                {...column.getHeaderProps([
                  {
                    className: clsx(column.className, {
                      [styles['column-header_active']]: column.isSorted,
                      [styles['column-header--inactive']]: !column.isSorted,
                    }),
                    style: column.style,
                  },
                  column.getSortByToggleProps(),
                ])}
              >
                {column.render('Header')}
                {column.canSort && (
                  <span
                    className={clsx('sorting-arrow', styles['sorting-arrow'], {
                      [styles['sorting-arrow--inactive']]: !column.isSorted,
                      [styles['sorting-arrow--desc']]: column.isSortedDesc,
                      [styles['sorting-arrow--asc']]: !column.isSortedDesc,
                    })}
                  >
                    <Icon
                      name={'arrow2-big'}
                      size={'s'}
                      className={clsx({
                        [styles['sorting-arrow_desc']]: !column.isSortedDesc,
                      })}
                    />
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}

        <div {...getTableBodyProps()}>
          {rows.map((row, ri) => {
            prepareRow(row)

            return (
              <div {...row.getRowProps()} key={'row-' + ri}>
                {row.cells.map((cell, ci) => {
                  return (
                    <div
                      key={'cell-' + ri + '-' + ci}
                      {...cell.getCellProps([
                        {
                          className: cell.column.className,
                          style: cell.column.style,
                        },
                        // getColumnProps(cell.column),
                        // getCellProps(cell),
                      ])}
                    >
                      {cell.render('Cell')}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>

        <div className="footer">
          {footerGroups.map((footerGroup, i) => (
            <div
              {...footerGroup.getHeaderGroupProps()}
              className="tr"
              key={'footer-group-' + i}
            >
              {footerGroup.headers.map((column, j) => (
                <div
                  {...column.getHeaderProps()}
                  className="td"
                  key={'footer-group-header-' + j}
                >
                  {column.render('Footer')}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

TableSticky.propTypes = {
  columns: PropTypes.array,
  tableData: PropTypes.array,
  tableLoading: PropTypes.bool,
  className: PropTypes.string,
  tableConfig: PropTypes.object,
  onSortingChange: PropTypes.func,
}

TableSticky.defaultProps = {
  columns: [],
  tableData: [],
  tableLoading: true,
  className: '',
  tableConfig: {},
}

export default TableSticky
